import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Truck, MapPin, CheckCircle2, XCircle, Loader2, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'placed', label: 'Placed', icon: Package },
  { key: 'packed', label: 'Packed', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

export default function TrackOrderPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/track/${orderNumber}`);
        setOrder(data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Order not found or not shipped yet.' : 'Something went wrong.');
      } finally { setLoading(false); }
    })();
  }, [orderNumber]);

  if (loading) return <Layout><div className="py-24 text-center"><Loader2 className="animate-spin mx-auto" /></div></Layout>;

  if (error) return (
    <Layout>
      <section className="py-20">
        <div className="max-w-md mx-auto px-6 text-center">
          <XCircle size={48} className="text-red-500 mx-auto" />
          <h1 className="h-display text-3xl mt-4">Tracking unavailable</h1>
          <p className="text-neutral-600 mt-3">{error}</p>
        </div>
      </section>
    </Layout>
  );

  const cancelled = order.shipment_status === 'cancelled';
  const currentIdx = STEPS.findIndex((s) => s.key === order.shipment_status);

  return (
    <Layout>
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <p className="eyebrow mb-4">Order tracking</p>
          <h1 className="h-display text-3xl md:text-4xl">Order <em className="font-serif-display italic font-normal text-navy">#{order.order_number}</em></h1>
          <p className="text-sm text-neutral-500 mt-1">Placed on {new Date(order.ordered_on).toLocaleDateString()}</p>

          <div className="card-soft p-6 mt-8">
            <p className="text-xs uppercase tracking-widest text-neutral-500">Items</p>
            <div className="mt-3 space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{it.name}</span>
                  <span className="text-neutral-500">×{it.qty}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card-soft p-6 mt-4">
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-6">Shipment status</p>
            {cancelled ? (
              <div className="flex items-center gap-3 text-red-600"><XCircle /> Order cancelled</div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  {STEPS.map((s, i) => (
                    <div key={s.key} className="flex-1 flex items-center gap-2">
                      <div className="flex flex-col items-center gap-2 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${i <= currentIdx ? 'bg-navy text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                          <s.icon size={16} />
                        </div>
                        <p className={`text-xs ${i <= currentIdx ? 'text-navy font-semibold' : 'text-neutral-400'}`}>{s.label}</p>
                      </div>
                      {i < STEPS.length - 1 && <div className={`flex-1 h-1 mb-6 rounded ${i < currentIdx ? 'bg-navy' : 'bg-neutral-200'}`}></div>}
                    </div>
                  ))}
                </div>

                {order.tracking_id && (
                  <div className="mt-6 p-4 rounded-xl bg-navy/5 flex items-start gap-3">
                    <MapPin size={16} className="text-navy flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-semibold">Tracking</p>
                      <p className="text-sm text-neutral-600">{order.tracking_id}{order.carrier ? ` · ${order.carrier}` : ''}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {order.shipment_history && order.shipment_history.length > 0 && (
            <div className="card-soft p-6 mt-4">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">History</p>
              <div className="space-y-3">
                {order.shipment_history.slice().reverse().map((h, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-navy mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-semibold capitalize">{h.status}</p>
                      <p className="text-xs text-neutral-500">{new Date(h.at).toLocaleString()}{h.note ? ` · ${h.note}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500 justify-center">
            <ShieldCheck size={13} className="text-navy" />
            Powered by Connect Near Me
          </div>
        </div>
      </section>
    </Layout>
  );
}
